import type React from "react"
import { useState, useEffect } from "react"
import { Box, Container, Typography, CircularProgress, useMediaQuery, useTheme, IconButton } from "@mui/material"
import { getUserDetails } from "../../apis/userApi"
import ProfileSection from "./ProfileSection"
import BookingsSection from "./BookingSection"
import { getBookingsByUser } from "../../apis/bookingApi"
import type { BookingsByUser } from "../../schemas/bookings"
import type { User } from "../../schemas/user"
import DashboardSidebar from "./Sidebar"
import MembershipsSection from "./MembershipsSection"
import PaymentsSection from "./PaymentsSection"
import SettingsSection from "./SettingsSections"
import ProgramEnrollmentsSection from "./ProgramEnrollmentsSection"
import ReferralsSection from "./ReferralsSection"
import { Menu as MenuIcon } from "@mui/icons-material"

export type DashboardSection = "profile" | "bookings" | "memberships" | "payments" | "settings" | "programs" | "referrals"

const Dashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState<DashboardSection>("profile")
  const [mobileOpen, setMobileOpen] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [bookings, setBookings] = useState<BookingsByUser[]>([])
  const [userLoading, setUserLoading] = useState(true)
  const [bookingsLoading, setBookingsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const token = localStorage.getItem("token")
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userData = await getUserDetails()
        setUser(userData)
      } catch (error) {
        console.error("Error fetching user details:", error)
        setError("Failed to load user details. Please refresh the page.")
      } finally {
        setUserLoading(false)
      }
    }

    fetchUserDetails()
  }, [])

  // Fetch bookings
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const bookingsData = await getBookingsByUser()
        setBookings(bookingsData)
      } catch (error) {
        console.error("Error fetching bookings:", error)
        setError("Failed to load bookings. Please refresh the page.")
      } finally {
        setBookingsLoading(false)
      }
    }

    fetchBookings()
  }, [token]) // Added token to the dependency array

  // Handle booking updates
  const handleBookingsUpdate = (updatedBookings: BookingsByUser[]) => {
    setBookings(updatedBookings)
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  // Render the active section component
  const renderActiveSection = () => {
    switch (activeSection) {
      case "profile":
        return userLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : user ? (
          <ProfileSection user={user} />
        ) : null

      case "bookings":
        return bookingsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <BookingsSection bookings={bookings} email={user?.email || ""} onBookingsUpdate={handleBookingsUpdate} />
        )

      case "memberships":
        return <MembershipsSection />

      case "payments":
        return <PaymentsSection />

      case "settings":
        return <SettingsSection user={user} />

      case "programs":
        return <ProgramEnrollmentsSection />

      case "referrals":
        return <ReferralsSection />

      default:
        return null
    }
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      {isMobile && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: "bold" }}>
            My Dashboard
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 3, justifyContent: "center", mx: "auto"}}>
        {/* Sidebar */}
        <DashboardSidebar
          activeSection={activeSection}
          onSectionChange={(section) => {
            setActiveSection(section)
            if (isMobile) {
              setMobileOpen(false)
            }
          }}
          mobileOpen={mobileOpen}
          onMobileToggle={handleDrawerToggle}
        />

        {/* Main content area */}
        <Box sx={{ flexGrow: 1, width: { xs: "100%", md: "calc(100% - 240px)" }}}>
          {/* {!isMobile && (
            <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ mb: 3, fontWeight: "bold" }}>
              My Dashboard
            </Typography>
          )} */}

          {renderActiveSection()}
        </Box>
      </Box>
    </Container>
  )
}

export default Dashboard
